import { Component } from '@theme/component';

/**
 * `<article-toc>` — table of contents for a blog article.
 *
 * Gives every h2/h3 in the article text a stable, readable id derived from its text
 * (e.g. `#durchfall-nach-tomaten`) and lists them. An id already set in the article HTML wins.
 * Google links straight to these anchors in search results, so ids must not depend on
 * heading position. The old positional anchors (`#heading-0` …) stay reachable through an
 * empty marker before each heading, so existing search-result links keep working.
 *
 * @extends {Component}
 */
class ArticleToc extends Component {
  connectedCallback() {
    super.connectedCallback();

    const content = document.getElementById('blog-post-contents');
    const list = this.querySelector('ol');
    if (!content || !list || list.childElementCount > 0) return;

    // Only article text headings — not the titles of product cards between the text chunks.
    const headings = content.querySelectorAll('.blog-post-content h2, .blog-post-content h3');
    const usedIds = new Set();
    /** @type {HTMLLIElement | null} */
    let currentItem = null;
    /** @type {HTMLOListElement | null} */
    let subList = null;

    headings.forEach((heading, index) => {
      const id = heading.id || uniqueId(slugify(heading.textContent ?? '') || `heading-${index}`, usedIds);
      heading.id = id;
      usedIds.add(id);

      const legacyId = `heading-${index}`;
      if (id !== legacyId && !document.getElementById(legacyId)) {
        const marker = document.createElement('span');
        marker.id = legacyId;
        marker.className = 'article-toc__legacy-anchor';
        heading.before(marker);
      }

      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = heading.textContent;

      const item = document.createElement('li');
      item.append(link);

      if (heading.tagName === 'H2') {
        item.className = 'toc-main';
        link.className = 'toc-main__link';
        list.append(item);
        currentItem = item;
        subList = null;
      } else if (currentItem) {
        if (!subList) {
          subList = document.createElement('ol');
          subList.className = 'toc-sub';
          currentItem.append(subList);
        }
        subList.append(item);
      }
    });
  }
}

/**
 * @param {string} text
 * @returns {string}
 */
function slugify(text) {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
}

/**
 * @param {string} base
 * @param {Set<string>} usedIds
 * @returns {string}
 */
function uniqueId(base, usedIds) {
  let id = base;
  let suffix = 2;
  while (usedIds.has(id) || document.getElementById(id)) {
    id = `${base}-${suffix}`;
    suffix += 1;
  }
  return id;
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}
